import type { TrainingAttemptRecord } from '../../lib/api';
import { buildPlatformScope, type PlatformScope } from '@examflow/core';
import {
  buildExecutableTrainingPrescription,
  buildAttemptHref,
  type ExamCode,
  type TcfModuleId,
  type TrainingIntensity,
  type UiTone,
} from '@examflow/learning-space';

const STABILITY_ACCURACY = 85;
const SWITCH_GAP = 12;

interface ModuleOption {
  moduleId: TcfModuleId;
  label: string;
  disabled?: boolean;
}

export interface TrainingRecordRecommendation {
  title: string;
  body: string;
  targetModuleId: TcfModuleId;
  targetModuleLabel: string;
  platformScope: PlatformScope | null;
  prescriptionLabel: string;
  sessionHref: string;
  actionLabel: string;
  tone: UiTone;
  averageAccuracy: number | null;
}

export function buildTrainingRecordRecommendation({
  records,
  selectedModuleId,
  moduleOptions,
  contentPack = 'tcf',
  selectedIntensity = 'standard',
  selectedLevel = '',
}: {
  records: TrainingAttemptRecord[];
  selectedModuleId: TcfModuleId;
  moduleOptions: ModuleOption[];
  contentPack?: ExamCode;
  selectedIntensity?: TrainingIntensity;
  selectedLevel?: string;
}): TrainingRecordRecommendation {
  const executableModuleOptions = moduleOptions.filter((module) => !module.disabled);
  const selectedModule = getModuleOption(executableModuleOptions, selectedModuleId);
  const activeScope = buildPlatformScope({ examType: contentPack });
  const scopedRecords = records.filter((record) => trainingRecordMatchesScope(record, activeScope));
  const moduleSummaries = executableModuleOptions
    .map((module) => summarizeModuleRecords(module, scopedRecords))
    .filter((summary): summary is ModuleAccuracySummary => summary !== null);
  const selectedSummary =
    moduleSummaries.find((summary) => summary.moduleId === selectedModuleId) ?? null;

  if (!selectedSummary) {
    return {
      title: '先建立本模块样本',
      body: `${selectedModule.label}还没有训练记录。先完成一轮训练，平台才能判断下一步是否要切换模块。`,
      targetModuleId: selectedModule.moduleId,
      targetModuleLabel: selectedModule.label,
      ...buildRecommendationExecution({
        contentPack,
        intensity: selectedIntensity,
        moduleId: selectedModule.moduleId,
        level: selectedLevel,
      }),
      actionLabel: `开始${selectedModule.label}`,
      tone: 'current',
      averageAccuracy: null,
    };
  }

  if (selectedSummary.averageAccuracy < STABILITY_ACCURACY) {
    return {
      title: '继续巩固当前模块',
      body: `${selectedSummary.label}最近${selectedSummary.recordCount}次平均正确率为 ${selectedSummary.averageAccuracy}%，还没有到稳定线。建议再做一组训练确认修正。`,
      targetModuleId: selectedSummary.moduleId,
      targetModuleLabel: selectedSummary.label,
      ...buildRecommendationExecution({
        contentPack,
        intensity: selectedIntensity,
        moduleId: selectedSummary.moduleId,
        level: selectedLevel,
      }),
      actionLabel: `继续${selectedSummary.label}`,
      tone: 'warning',
      averageAccuracy: selectedSummary.averageAccuracy,
    };
  }

  const weakestOtherModule = moduleSummaries
    .filter((summary) => summary.moduleId !== selectedModuleId)
    .sort((a, b) => a.averageAccuracy - b.averageAccuracy)[0];

  if (
    weakestOtherModule &&
    weakestOtherModule.averageAccuracy <= selectedSummary.averageAccuracy - SWITCH_GAP
  ) {
    return {
      title: '切换到薄弱模块',
      body: `${selectedSummary.label}最近表现已稳定。${weakestOtherModule.label}最近平均正确率为 ${weakestOtherModule.averageAccuracy}%，更适合作为下一轮训练重点。`,
      targetModuleId: weakestOtherModule.moduleId,
      targetModuleLabel: weakestOtherModule.label,
      ...buildRecommendationExecution({
        contentPack,
        intensity: selectedIntensity,
        moduleId: weakestOtherModule.moduleId,
        level: selectedLevel,
      }),
      actionLabel: `切换到${weakestOtherModule.label}`,
      tone: 'current',
      averageAccuracy: weakestOtherModule.averageAccuracy,
    };
  }

  return {
    title: '保持当前节奏',
    body: `${selectedSummary.label}最近${selectedSummary.recordCount}次平均正确率为 ${selectedSummary.averageAccuracy}%。可以继续当前模块，也可以根据诊断页查看更细的薄弱点。`,
    targetModuleId: selectedSummary.moduleId,
    targetModuleLabel: selectedSummary.label,
    ...buildRecommendationExecution({
      contentPack,
      intensity: selectedIntensity,
      moduleId: selectedSummary.moduleId,
      level: selectedLevel,
    }),
    actionLabel: `继续${selectedSummary.label}`,
    tone: 'success',
    averageAccuracy: selectedSummary.averageAccuracy,
  };
}

function trainingRecordMatchesScope(
  record: TrainingAttemptRecord,
  activeScope: PlatformScope | null
): boolean {
  if (!activeScope) return true;

  if (record.platformScope) {
    return (
      record.platformScope.languageId === activeScope.languageId &&
      record.platformScope.examType === activeScope.examType
    );
  }

  return (
    String(record.languageId ?? '').toLowerCase() === activeScope.languageId &&
    String(record.examType || record.contentPack || '').toLowerCase() === activeScope.examType
  );
}

interface ModuleAccuracySummary {
  moduleId: TcfModuleId;
  label: string;
  recordCount: number;
  averageAccuracy: number;
}

function summarizeModuleRecords(
  module: ModuleOption,
  records: TrainingAttemptRecord[]
): ModuleAccuracySummary | null {
  const moduleRecords = records
    .filter((record) => record.moduleId === module.moduleId)
    .sort((a, b) => b.completedAt - a.completedAt)
    .slice(0, 3);

  if (moduleRecords.length === 0) {
    return null;
  }

  const averageAccuracy = Math.round(
    moduleRecords.reduce((total, record) => total + record.accuracy, 0) / moduleRecords.length
  );

  return {
    moduleId: module.moduleId,
    label: module.label,
    recordCount: moduleRecords.length,
    averageAccuracy,
  };
}

function getModuleOption(moduleOptions: ModuleOption[], moduleId: TcfModuleId): ModuleOption {
  return (
    moduleOptions.find((module) => module.moduleId === moduleId) ?? {
      moduleId,
      label: moduleId,
    }
  );
}

function buildRecommendationExecution({
  contentPack,
  intensity,
  moduleId,
  level = '',
}: {
  contentPack: ExamCode;
  intensity: TrainingIntensity;
  moduleId: TcfModuleId;
  level?: string;
}): Pick<TrainingRecordRecommendation, 'platformScope' | 'prescriptionLabel' | 'sessionHref'> {
  const prescription = buildExecutableTrainingPrescription({
    contentPack,
    moduleId,
    intensity,
  });
  const languageId =
    prescription.platformScope?.languageId ??
    buildPlatformScope({ examType: contentPack })?.languageId;
  const sessionHref = languageId
    ? buildAttemptHref({
        kind: 'training',
        languageId,
        examType: contentPack,
        skillId: moduleId,
        intensity,
        level: level || undefined,
      })
    : prescription.launchHref;

  return {
    platformScope: prescription.platformScope,
    prescriptionLabel: prescription.prescriptionLabel,
    sessionHref,
  };
}
